// Baselayer Results Explorer — Litigations tab. Reads the SHARED docket add-on
// (ordered as either Litigations or Bankruptcies) and filters to NON-bankruptcy
// dockets. Once ordered, an All/Exact/Similar sub-filter on match level; rows
// link to the litigation detail page.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  StatusBadge,
} from "@/design-system/components/ui";
import type { BackgroundCheckAddOnDto } from "@/api/backgroundChecks/addOnsApi";
import { AddOnEmptyState } from "../AddOnEmptyState";
import { findAddOn, isCompleted, formatLooseDate } from "../tabHelpers";
import { parseDockets, type ParsedDocket } from "../parse";

interface LitigationsTabProps {
  checkId: string;
  addOns: BackgroundCheckAddOnDto[] | undefined;
}

type SubFilter = "all" | "exact" | "similar";

export function LitigationsTab({ checkId, addOns }: LitigationsTabProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<SubFilter>("all");
  // Litigations + Bankruptcies share one docket add-on.
  const addOn = findAddOn(addOns, "Litigations", "Bankruptcies");

  if (!addOn || !isCompleted(addOn)) {
    return (
      <AddOnEmptyState
        checkId={checkId}
        addOnType="Litigations"
        label="Litigations"
        title="No litigations ordered"
        description="Order a docket search to see litigation records for this subject."
        addOn={addOn}
      />
    );
  }

  const dockets = parseDockets(addOn.rawResponseJson).filter(
    (d) => !d.isBankruptcy,
  );
  const filtered = dockets.filter((d) => matchesFilter(d, filter));

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex gap-space-1">
        {(["all", "exact", "similar"] as SubFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            data-active={filter === f || undefined}
            className={
              "rounded-pill border px-space-3 py-[2px] text-caption font-medium capitalize transition-colors " +
              (filter === f
                ? "border-gold bg-gold/15 text-onyx"
                : "border-border-light bg-white-warm text-gray-mid hover:text-onyx")
            }
          >
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No Litigations Found"
          description="No litigation dockets matched this subject."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Status</TH>
              <TH>Filing date</TH>
              <TH>Name / docket #</TH>
              <TH>Court</TH>
              <TH>Case type</TH>
            </TR>
          </THead>
          <TBody>
            {filtered.map((d, i) => (
              <TR
                key={d.id ?? i}
                className="cursor-pointer"
                onClick={() =>
                  d.id &&
                  navigate(
                    `/background-checks/${checkId}/litigations/${d.id}`,
                  )
                }
              >
                <TD>
                  {d.status ? (
                    <StatusBadge tone="info">{d.status}</StatusBadge>
                  ) : (
                    "—"
                  )}
                </TD>
                <TD>{formatLooseDate(d.dateFiled)}</TD>
                <TD>
                  <span className="block">{d.title ?? "—"}</span>
                  {d.docketNumber && (
                    <span className="block text-caption text-gray-mid">
                      {d.docketNumber}
                    </span>
                  )}
                </TD>
                <TD>{d.court ?? "—"}</TD>
                <TD>{d.caseType ?? "—"}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function matchesFilter(d: ParsedDocket, filter: SubFilter): boolean {
  if (filter === "all") return true;
  return (d.matchLevel ?? "").toLowerCase().includes(filter);
}
